'use client'

import { useState, useRef, useEffect, memo } from 'react'

import type { GameLetter, GamePosition } from '../types'

import { cn } from '@/shared/lib/utils'

interface SquareProps {
  letter: GameLetter
  isActive: boolean
  isFound: boolean
  onClick: (position: GamePosition) => void
  onEnter: (position: GamePosition) => void
  isCompleted?: boolean
  selectionError?: boolean
  selectionSuccess?: boolean
  className?: string
}

export const Square = memo<SquareProps>(function Square({
  letter,
  isActive,
  isFound,
  onClick,
  onEnter,
  isCompleted = false,
  selectionError = false,
  selectionSuccess = false,
  className
}) {
  const [fontSize, setFontSize] = useState(0)
  const squareRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const calc = () => {
      const rect = squareRef.current?.getBoundingClientRect()
      if (!rect) return
      setFontSize(Math.max(rect.width * 0.54, 14))
    }
    calc()
    window.addEventListener('resize', calc)
    return () => window.removeEventListener('resize', calc)
  }, [])

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0 || isCompleted) return
    onClick({ row: letter.row, column: letter.column })
  }

  const handleMouseEnter = () => {
    if (isCompleted) return
    onEnter({ row: letter.row, column: letter.column })
  }

  const base =
    'relative w-full h-full flex items-center justify-center border-2 rounded-md select-none transition-all duration-200 touch-none'
  const insetShadow =
    'shadow-[inset_0_-3px_0_0_rgba(255,255,255,0.4),inset_0_-1px_0_0_rgba(0,0,0,0.1)]'
  const completedShadow =
    'shadow-[0_3px_0_0_rgba(255,255,255,0.2),inset_0_1px_0_0_rgba(0,0,0,0.3)]'
  const activeShadow =
    'shadow-[inset_0_-3px_0_0_rgba(255,255,255,0.3),inset_0_1px_0_0_rgba(0,0,0,0.2)]'
  const hoverShadow =
    'hover:shadow-[inset_0_-3px_0_0_rgba(255,255,255,0.5),inset_0_-2px_0_0_rgba(0,0,0,0.1)]'
  const errorShadow =
    'shadow-[inset_0_-3px_0_0_rgba(255,255,255,0.2),inset_0_1px_0_0_rgba(0,0,0,0.3)]'
  const successShadow =
    'shadow-[inset_0_-3px_0_0_rgba(255,255,255,0.4),inset_0_1px_0_0_rgba(0,0,0,0.1)]'

  return (
    <div
      ref={squareRef}
      data-square-row={letter.row}
      data-square-col={letter.column}
      className={cn(
        base,
        insetShadow,
        // Estado completado (prioridade máxima)
        isCompleted && [
          'bg-blue-700 border-blue-700 text-white cursor-not-allowed',
          completedShadow
        ],
        // Estado ativo com erro (prioridade alta)
        !isCompleted &&
          selectionError &&
          isActive && [
            'bg-red-100 border-red-500 text-red-700 cursor-pointer',
            errorShadow
          ],
        // Estado ativo com sucesso (prioridade alta)
        !isCompleted &&
          selectionSuccess &&
          isActive && [
            'bg-green-100 border-green-500 text-green-700 cursor-pointer',
            successShadow
          ],
        // Estado ativo normal (prioridade média)
        !isCompleted &&
          !selectionError &&
          !selectionSuccess &&
          isActive && [
            'bg-blue-100 border-blue-500 text-blue-700 cursor-pointer',
            activeShadow
          ],
        // Estado encontrado (prioridade média)
        !isCompleted &&
          isFound &&
          !isActive && [
            'bg-blue-100 border-blue-500 text-blue-700 cursor-pointer',
            activeShadow
          ],
        // Estado inativo normal (prioridade baixa)
        !isCompleted &&
          !isActive &&
          !isFound && [
            'bg-white border-gray-300 text-gray-700 cursor-pointer hover:bg-gray-50',
            hoverShadow
          ],
        className
      )}
      // mouse (desktop). No mobile, o Board captura via Pointer Events
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
    >
      <span
        className="font-bold select-none relative z-40"
        style={{ fontSize: `${fontSize}px` }}
      >
        {letter.letter}
      </span>
    </div>
  )
})

Square.displayName = 'Square'
